import { useEffect, useState, useContext } from 'react';
import { Views, ViewValues } from '@/constants';
import {
  ConfigContext,
  PrivacySettingsContext,
  WidgetStateContext,
} from '@/context';
import { Card } from '@/components/ui/Card';
import { handleError } from '@/utils/error';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { toTitleCase } from '@concord-platform/text-utils';
import { ConsentCategoryItem } from '@/types';
import { cn } from '@/utils/cn';

type Props = {
  setSelectedView: (view: ViewValues) => void;
  setConsentCategory: (category: ConsentCategoryItem) => void;
};

export function ConsentManagement({
  setSelectedView,
  setConsentCategory,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<ConsentCategoryItem[]>([]);

  const {
    currentConsentState,
    gpcSignal,
    privacySettingCategories = [],
    updatePrivacySettings,
  } = useContext(PrivacySettingsContext);
  const { toggleOpen, setBannerClosed } = useContext(WidgetStateContext);
  const {
    consentCategories,
    consentSettingsAdditionalDetailsButton,
    consentSettingsAlwaysActive,
    consentSettingsTitle,
    consentSettingsText,
    consentSettingsConfirmButton,
    privacyCenterTheme,
  } = useContext(ConfigContext);

  /* Accordion state */
  const [expandedItems, setExpandedItems] = useState<boolean[]>(
    Array.from({ length: categories.length }, () => false),
  );

  const toggleItem = (index: number) => {
    const newExpandedItems = [...expandedItems];
    newExpandedItems[index] = !newExpandedItems[index];
    setExpandedItems(newExpandedItems);
  };

  const addConsentEvents = async () => {
    try {
      setLoading(true);
      const consentState = Object.fromEntries(
        categories.map(({ subcategory, consentState }) => [
          subcategory,
          consentState,
        ]),
      );
      updatePrivacySettings({
        consentState,
        gpcSignal,
      });
    } catch (err) {
      handleError(err);
    }
    setBannerClosed(true);
    toggleOpen();
    setLoading(false);
  };

  const updateConsentCategories = (updated: ConsentCategoryItem) => {
    const updatedCategories: ConsentCategoryItem[] = categories.map((cat) => {
      if (
        updated.subcategory === 'do_not_sell' &&
        updated.consentState === 'accepted' &&
        cat.subcategory === 'marketing'
      ) {
        return {
          ...cat,
          consentState: 'declined',
        };
      }
      if (updated.subcategory === cat.subcategory) {
        return updated;
      }
      return cat;
    });

    setCategories(
      updatedCategories.sort((a, b) => a.displayOrder - b.displayOrder),
    );
  };

  useEffect(() => {
    setCategories(
      privacySettingCategories.map((item) => ({
        ...item,
        consentState: currentConsentState[item.subcategory],
      })),
    );
  }, [privacySettingCategories, currentConsentState]);

  return (
    <div className={cn('space-y-6', privacyCenterTheme)}>
      <Card
        button={
          <Button
            className="text-primary border-primary hover:text-foreground hover:border-foreground"
            data-testid="cd-confirm-choices-button"
            variant="pc-outline"
            onClick={() => addConsentEvents()}
          >
            {consentSettingsConfirmButton}
          </Button>
        }
        loading={loading}
        title={consentSettingsTitle}
      >
        <p
          className="text-muted-foreground mb-6"
          data-testid="cd-consent-settings-description"
        >
          {consentSettingsText}
        </p>
        <Accordion className="w-full" role="tablist" type="multiple">
          <div className="space-y-4">
            {categories.map((category, index) => (
              <AccordionItem
                key={category.name}
                className="border-card-border rounded-button border bg-transparent"
                value={category.name}
              >
                <div className="relative">
                  <AccordionTrigger
                    aria-controls={`accordion-content-${category.subcategory}`}
                    aria-expanded={expandedItems[index] ?? false}
                    className="hover:text-primary rounded-button min-h-12 hover:shadow-md"
                    onClick={() => {
                      toggleItem(index);
                    }}
                  >
                    <span className="text-subheader flex-1 pr-10 text-left text-sm font-semibold">
                      {consentCategories[category.subcategory].title}
                    </span>
                    {category.subcategory === 'strictly_necessary' && (
                      <span className="text-primary text-center text-sm font-semibold">
                        {consentSettingsAlwaysActive}
                      </span>
                    )}
                  </AccordionTrigger>
                  {category.subcategory !== 'strictly_necessary' && (
                    <div
                      className="absolute right-4 top-0 flex h-full items-center"
                      id={toTitleCase(`${category.subcategory} Consent`)}
                    >
                      <Switch
                        aria-labelledby={toTitleCase(
                          `${category.subcategory} Consent`,
                        )}
                        checked={category.consentState === 'accepted'}
                        data-testid={`cd-switch-${category.subcategory}`}
                        // @ts-ignore
                        disabled={category.subcategory === 'strictly_necessary'}
                        role="switch"
                        onCheckedChange={(checked) => {
                          updateConsentCategories({
                            ...category,
                            consentState: checked ? 'accepted' : 'declined',
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
                <AccordionContent
                  className="p-4"
                  id={`accordion-content-${category.subcategory}`}
                >
                  <div>
                    <p className="text-muted-foreground">
                      {consentCategories[category.subcategory].text}
                    </p>
                    {(category.subcategory === 'strictly_necessary' ||
                      category.subcategory === 'analytics' ||
                      category.subcategory === 'marketing' ||
                      category.subcategory === 'functional' ||
                      category.subcategory === 'unclassified') && (
                      <div className="align-center mt-2 flex justify-start">
                        <span
                          className="hover:text-primary text-subheader block cursor-pointer font-semibold underline"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setConsentCategory(category);
                            setSelectedView(Views.ConsentCategoryDetail);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setConsentCategory(category);
                              setSelectedView(Views.ConsentCategoryDetail);
                            }
                          }}
                        >
                          {consentSettingsAdditionalDetailsButton}
                        </span>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </div>
        </Accordion>
      </Card>
    </div>
  );
}
